"use client";

import Image from "next/image";
import Link from "next/link";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductCompareButton } from "@/components/ProductCompareButton";
import { ProductFavoriteButton } from "@/components/ProductFavoriteButton";
import { type Product, getProductImage, getProductImageAlt } from "@/lib/catalog";
import { extractProductDiameter } from "@/lib/product-merchandising";
import { fixMaybeBrokenText } from "@/lib/text-client";

type ProductCardProps = {
  product: Product;
  showRetailPurchase?: boolean;
  showB2B?: boolean;
  showRental?: boolean;
  showRepair?: boolean;
  mode?: "sale" | "rental";
  priceLabel?: string | null;
  availabilityLabel?: string;
  primaryActionHref?: string;
  primaryActionLabel?: string;
};

function formatPrice(value: number) {
  return `${new Intl.NumberFormat("ru-RU").format(value)} ₽`;
}

function getDiscountPercent(product: Product) {
  if (!product.oldPrice || product.oldPrice <= product.price) {
    return null;
  }

  return Math.round(((product.oldPrice - product.price) / product.oldPrice) * 100);
}

export function ProductCard({
  product,
  showRetailPurchase = true,
  mode = "sale",
  priceLabel,
  availabilityLabel,
  primaryActionHref,
  primaryActionLabel
}: ProductCardProps) {
  const productHref = `/product/${product.slug}`;
  const diameterLabel = extractProductDiameter(product);
  const discountPercent = getDiscountPercent(product);
  const isRental = mode === "rental" || product.isRentalOnly;
  const canAddToCart = !isRental && showRetailPurchase;
  const cardPriceLabel = priceLabel || formatPrice(product.price);
  const cardAvailabilityLabel =
    availabilityLabel || (product.stockQty > 0 ? "Есть в наличии" : "Под заказ");
  const buttonHref = primaryActionHref || productHref;
  const buttonLabel = primaryActionLabel || (isRental ? "Забронировать" : "Подробнее");

  return (
    <article className="product-card product-card-v2">
      <div className="product-card-v2__toolbar">
        <div className="product-card-v2__badges">
          {discountPercent ? <span className="product-card-v2__badge">-{discountPercent}%</span> : null}
          {diameterLabel ? <span className="product-card-v2__chip">Диаметр {diameterLabel}</span> : null}
        </div>

        <div className="product-card-v2__actions">
          <ProductCompareButton product={product} />
          <ProductFavoriteButton product={product} />
        </div>
      </div>

      <Link className="product-card-v2__media" href={productHref}>
        <Image
          alt={fixMaybeBrokenText(getProductImageAlt(product))}
          height={156}
          loading="lazy"
          quality={44}
          sizes="(max-width: 640px) 34vw, (max-width: 1100px) 17vw, 152px"
          src={getProductImage(product)}
          width={156}
        />
      </Link>

      <div className="product-card-v2__body">
        <span className="product-card-v2__sku">Код {product.sku ?? "без артикула"}</span>

        <div className="product-card-v2__price-row">
          <strong className="product-card-v2__price">{cardPriceLabel}</strong>
          {product.oldPrice > 0 && product.oldPrice > product.price ? (
            <span className="product-card-v2__old-price">{formatPrice(product.oldPrice)}</span>
          ) : null}
        </div>

        <span
          className={`product-card-v2__availability ${
            product.stockQty > 0 ? "product-card-v2__availability--ok" : "product-card-v2__availability--muted"
          }`}
        >
          {fixMaybeBrokenText(cardAvailabilityLabel)}
        </span>

        <Link className="product-card-v2__title" href={productHref}>
          {fixMaybeBrokenText(product.name)}
        </Link>
      </div>

      <div className="product-card-v2__footer">
        {canAddToCart ? (
          <AddToCartButton
            className="button product-card-v2__cta"
            item={{
              id: product.id,
              slug: product.slug,
              sku: product.sku,
              name: fixMaybeBrokenText(product.name),
              image: getProductImage(product),
              price: product.price,
              oldPrice: product.oldPrice,
              unit: product.unit
            }}
          />
        ) : (
          <Link className="button product-card-v2__cta" href={buttonHref}>
            {buttonLabel}
          </Link>
        )}
      </div>
    </article>
  );
}
